import { Checkbox, Divider, Typography } from '@trackman/web-shared-components';

import { ReactSortable } from 'react-sortablejs';
import { ReactComponent as SixDotsIcon } from 'svg/SixDots.svg';
import { VisibleStatDto } from 'types/CodegenReportTypes';
import { useTranslation } from 'react-i18next';

type Props = {
  label: string;
  draggableCheckboxes: VisibleStatDto[];
  setDraggableCheckboxes: (draggableCheckboxes: VisibleStatDto[]) => void;
  disabled?: boolean;
  disabledKeys?: string[];
  useShortLabels?: boolean;
};

const VisibleStatCheckbox = ({
  option,
  useShortLabel,
  disabled,
  onChange,
}: {
  option: VisibleStatDto;
  useShortLabel: boolean;
  disabled?: boolean;
  onChange?: () => void;
}) => {
  const { t } = useTranslation();

  const label = t(option.key);
  const shortLabel = useShortLabel && t('short.' + option.key);

  return (
    <Checkbox
      label={useShortLabel ? shortLabel : label}
      hint={useShortLabel && label !== shortLabel && label}
      checked={option.checked}
      disabled={disabled}
      onChange={onChange}
    />
  );
};

const GenericDraggableCheckboxConfig = ({
  label,
  draggableCheckboxes,
  setDraggableCheckboxes,
  disabled = false,
  disabledKeys = [],
  useShortLabels = false,
}: Props) => {
  const { t } = useTranslation();

  const enabledDraggableCheckboxes = draggableCheckboxes.filter((option) => !disabled && !disabledKeys.includes(option.key));
  const disabledDraggableCheckboxes = draggableCheckboxes.filter((option) => disabled || disabledKeys.includes(option.key));

  return (
    <>
      <Typography variant='label bold' component='div' color='#A0A0A0'>
        {t(label)}
      </Typography>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        {disabledDraggableCheckboxes
          .sort((a, b) => a.order - b.order)
          .map((option) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }} key={option.key}>
              <div style={{ height: 16, color: 'transparent' }}>
                <SixDotsIcon />
              </div>
              <VisibleStatCheckbox option={option} useShortLabel={useShortLabels} disabled={true} onChange={() => {}} />
            </div>
          ))}
        <ReactSortable
          list={enabledDraggableCheckboxes.sort((a, b) => a.order - b.order).map((option) => ({ id: option.key }))}
          setList={(list) => {
            const newCheckboxes = draggableCheckboxes.map((option) => ({
              ...option,
              order: list.findIndex((item) => item.id === option.key),
            }));
            setDraggableCheckboxes(newCheckboxes);
          }}
          style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}
        >
          {enabledDraggableCheckboxes.map((option) => (
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }} key={option.key}>
              <div style={{ cursor: 'grab', height: 16, color: '#a0a0a0' }}>
                <SixDotsIcon />
              </div>
              <VisibleStatCheckbox
                option={option}
                useShortLabel={useShortLabels}
                onChange={() => {
                  const copy = [...draggableCheckboxes];
                  copy.find((item) => item.key === option.key)!.checked = !option.checked;
                  setDraggableCheckboxes(copy);
                }}
              />
            </div>
          ))}
        </ReactSortable>
      </div>
      <Divider />
    </>
  );
};

export default GenericDraggableCheckboxConfig;
